import { useMemo, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { catcat as cc } from "cerulean";
import { IconButton, Row, Table, useBreakpoints } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { useUserFeatures } from "../../contexts/UserFeaturesContext";
import { useCurrentUser } from "../../contexts/CurrentUserContext";
import WireStatusesDialog from "../../accounts/WireStatusesDialog";
import StatusDefinitionsDialog from "../FileImport/StatusDefinitionsDialog";

type DrawerControlsProps = {
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
  isOpen: boolean;
};

export type RenderDrawerProps<T> = (
  item: T | null,
  itemId: API.PaymentActivity["object_uuid"] | API.BulkPayment["id"] | null,
  controls: DrawerControlsProps
) => React.ReactNode;

type TableWithDrawerProps<T extends { uuid: string }> = {
  items: T[];
  renderRow: (item: T) => React.ReactNode;
  renderDrawer: RenderDrawerProps<T>;
  basePath: string;
  paramKey: "activityUuid" | "bulkPaymentId" | "fileImportId";
  getItemRoute?: (item: T) => string;
};

const TableWithDrawer = <T extends { uuid: string }>({
  items,
  renderRow,
  renderDrawer,
  basePath,
  paramKey,
  getItemRoute,
}: TableWithDrawerProps<T>) => {
  const { l10n } = useLocalization();
  const { m } = useBreakpoints();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const features = useUserFeatures();
  const { currentUser } = useCurrentUser();

  const { ach_payments_collect: achPaymentsCollectFeature } = features;
  const isFileImport = basePath.includes("file-import");
  const isIndividualPaymentActivity = basePath === "payment_activity";
  const showWireStatusDialog =
    isIndividualPaymentActivity && !currentUser?.isBusiness();
  const showStatusInfoButton = isFileImport || showWireStatusDialog;
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const selectedId =
    (params[paramKey] as
      | API.PaymentActivity["object_uuid"]
      | API.BulkPayment["id"]
      | undefined) ?? null;
  const selectedIndex = useMemo(
    () => items.findIndex((p) => p.uuid === selectedId),
    [items, selectedId]
  );
  const selectedItemFromTable = items[selectedIndex] ?? null;
  const isDrawerOpen = Boolean(selectedId);

  const handleCloseDrawer = () => navigate(`/${basePath}${location.search}`);

  const handleClickUp = () =>
    selectedIndex > 0
      ? navigate(
          `${
            getItemRoute?.(items[selectedIndex - 1]) ??
            `/${basePath}/${items[selectedIndex - 1].uuid}`
          }${location.search}`
        )
      : handleCloseDrawer();

  const handleClickDown = () =>
    selectedIndex < items.length - 1
      ? navigate(
          `${
            getItemRoute?.(items[selectedIndex + 1]) ??
            `/${basePath}/${items[selectedIndex + 1].uuid}`
          }${location.search}`
        )
      : handleCloseDrawer();

  const isBulkLike =
    basePath.includes("bulk") || basePath.includes("file-import");

  const getAmountHeader = () => {
    if (isBulkLike && achPaymentsCollectFeature) {
      return l10n.getString("label-net-total");
    }
    return l10n.getString("label-amount");
  };

  return (
    <>
      <Table
        colVisibility={
          isFileImport
            ? [
                "*", // description
                "m", // status only displayed on >M screens
                "l", // account only displayed on >L screens
                "l", // method
                "*", // amount
                "m", // kebab button only displayed on >M screens
              ]
            : [
                "*", // description
                "m", // status only displayed on >M screens
                "l", // account only displayed on >L screens
                "*", // amount
                "m", // kebab button only displayed on >M screens
              ]
        }
        colLayout={
          isFileImport
            ? {
                s: "1fr min-content",
                m: "1fr 28% 15% 5%",
                l: "35% 18% 22% 10% 10% 5%",
              }
            : {
                s: "1fr min-content",
                m: "1fr 28% 15% 5%",
                l: "40% 20% 25% 10% 5%",
              }
        }
      >
        {m ? (
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                <span className={cc({ "margin--left--l": m })}>
                  {l10n.getString("label-description")}
                </span>
              </Table.HeaderCell>
              <Table.HeaderCell>
                <Row alignItems="center" gapSize="none">
                  {l10n.getString("payment-activity-label-status")}
                  {showStatusInfoButton && (
                    <IconButton
                      name="info"
                      onClick={() => setIsStatusDialogOpen(true)}
                      kind="action"
                      style={{ display: "inline-flex", alignSelf: "center" }}
                    />
                  )}
                </Row>
              </Table.HeaderCell>
              <Table.HeaderCell>
                {l10n.getString("label-account")}
              </Table.HeaderCell>
              {isFileImport && (
                <Table.HeaderCell>
                  {l10n.getString("payment-activity-label-method")}
                </Table.HeaderCell>
              )}
              <Table.HeaderCell textAlign={"right" as any}>
                {getAmountHeader()}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
        ) : null}
        <Table.Body>{items.map((item) => renderRow(item))}</Table.Body>
      </Table>
      {isDrawerOpen && selectedId
        ? renderDrawer(selectedItemFromTable, selectedId, {
            onClose: handleCloseDrawer,
            onNext: handleClickDown,
            onPrev: handleClickUp,
            isOpen: isDrawerOpen,
          })
        : null}
      {isFileImport ? (
        <StatusDefinitionsDialog
          isOpen={isStatusDialogOpen}
          onClose={() => setIsStatusDialogOpen(false)}
        />
      ) : null}
      {showWireStatusDialog ? (
        <WireStatusesDialog
          isDialogOpen={isStatusDialogOpen}
          closeDialog={() => setIsStatusDialogOpen(false)}
        />
      ) : null}
    </>
  );
};

export { TableWithDrawer };
export default TableWithDrawer;
